import React from 'react';
import './fill-point.less';
import { isNull, isUndefined } from 'lodash';
import { Select } from '@meitu/saas-ui';
import { IMarkupLayerProps, IMarkupPoint } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { getMarkupPointList } from '@/editor/point-config';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useStoreState } from '@/store';

const { Option } = Select;

export const FillPointFeature: React.FC<{
    id?: string;
    startPoint?: IMarkupPoint;
    endPoint?: IMarkupPoint;
    onAfterChange?: Function;
    style?: React.CSSProperties;
    trackCode?: {
        point?: string | number;
        swap?: string | number;
    };
}> = React.memo(({ style, id, startPoint, endPoint, onAfterChange, trackCode }) => {
    const { $t } = useLocaleStore();
    const theme = useStoreState((state) => state.editorModel.theme);
    const needListener = !isUndefined(id);
    const select1GlobalWidgetRef = useGlobalWidget(editor, false);
    const select2GlobalWidgetRef = useGlobalWidget(editor, false);
    const res = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            const { startPoint, endPoint } = layer.data as IMarkupLayerProps;
            return { startPoint, endPoint };
        },
        true,
        needListener,
    );

    if (needListener) {
        if (isNull(res)) {
            return null;
        }
        startPoint = res.startPoint;
        endPoint = res.endPoint;
    } else {
        if (isUndefined(startPoint) || isUndefined(endPoint)) return null;
    }

    return (
        <div className="c-markup-panel__item" style={{ ...style }}>
            <LabelAction title={$t((v) => v.panel.markupPanel.endpoint)} align={true}>
                <div className="c-markup-panel__point">
                    <div style={{ width: 70 }}>
                        <Select
                            onDropdownVisibleChange={(e) => {
                                select1GlobalWidgetRef.current = e;
                            }}
                            value={startPoint.type}
                            className="c-markup-panel__dasharray"
                            suffixIcon={
                                <IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />
                            }
                            onChange={(value: any, option: any) => {
                                const { url, offset, width, height } = option;
                                if (id) {
                                    const layer = editor.stage.getLayer(id, true);
                                    editor.markupLayer.setAttrOperateOriginData(layer);
                                    editor.markupLayer.setPoint(
                                        id,
                                        {
                                            url,
                                            type: value,
                                            offset,
                                            width,
                                            height,
                                        },
                                        true,
                                    );
                                    editor.markupLayer.clearAttrOperateOriginData();
                                }
                                onAfterChange?.(trackCode?.point, {
                                    operate: 'left',
                                    url,
                                    type: value,
                                    offset,
                                    width,
                                    height,
                                });
                            }}
                        >
                            {getMarkupPointList().map((item, index) => {
                                return (
                                    <Option
                                        key={index}
                                        url={item.url}
                                        offset={item.offset}
                                        width={item.width}
                                        height={item.height}
                                        value={item.value}
                                    >
                                        <div className="c-markup-panel__img-wrap">
                                            <img alt="" src={(item as any)[theme]} width={50} height={20} />
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                    <div
                        className="c-markup-panel__icon"
                        onClick={() => {
                            if (id) {
                                const layer = editor.stage.getLayer(id, true);
                                editor.markupLayer.setAttrOperateOriginData(layer);
                                editor.markupLayer.swapPoint(id);
                                editor.markupLayer.clearAttrOperateOriginData();
                            }
                            onAfterChange?.(trackCode?.swap, {
                                operate: 'swap',
                            });
                        }}
                    >
                        <IconFont name="ReplaceBold" fontSize={16} color="var(--content-system-primary)" />
                    </div>
                    <div style={{ width: 70 }}>
                        <Select
                            onDropdownVisibleChange={(e) => {
                                select2GlobalWidgetRef.current = e;
                            }}
                            value={endPoint.type}
                            className="c-markup-panel__dasharray"
                            suffixIcon={
                                <IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />
                            }
                            onChange={(value: any, option: any) => {
                                const { url, offset, width, height } = option;
                                if (id) {
                                    const layer = editor.stage.getLayer(id, true);
                                    editor.markupLayer.setAttrOperateOriginData(layer);
                                    editor.markupLayer.setPoint(
                                        id,
                                        {
                                            url,
                                            type: value,
                                            offset,
                                            width,
                                            height,
                                        },
                                        false,
                                    );
                                    editor.markupLayer.clearAttrOperateOriginData();
                                }
                                onAfterChange?.(trackCode?.point, {
                                    operate: 'right',
                                    url,
                                    type: value,
                                    offset,
                                    width,
                                    height,
                                });
                            }}
                        >
                            {getMarkupPointList().map((item, index) => {
                                return (
                                    <Option
                                        key={index}
                                        url={item.url}
                                        offset={item.offset}
                                        width={item.width}
                                        height={item.height}
                                        value={item.value}
                                    >
                                        <div className="c-markup-panel__img-wrap">
                                            <img
                                                alt=""
                                                style={{
                                                    transform: 'rotateY(180deg)',
                                                }}
                                                src={(item as any)[theme]}
                                                width={50}
                                                height={20}
                                            />
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>
            </LabelAction>
        </div>
    );
});
